import React, { useState, useEffect } from 'react';
import { Building2, Palette, User, Shield, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { get, post, patch, mfaSetup, mfaEnable, mfaDisable } from '../services/api';
import PasswordInput from '../components/PasswordInput';
import type { AdminUser } from '../hooks/useAuth';
import { BRAND_NAME, BRAND_SUB } from '../utils/brand';

interface Props { user: AdminUser; }

interface Profile {
  id: string; user_name: string; employee_code: string; email: string;
  user_role: string; short_id: string; last_login_at: string | null; created_at: string;
  company_name: string; company_subdomain: string; website_url?: string | null;
  company_address?: string | null; company_tel?: string | null; company_rep?: string | null;
  primary_color?: string | null; logo_url?: string | null;
}

const ROLE_LABEL: Record<string, string> = {
  system_admin: 'システム管理者', shore_admin: '陸上管理者', captain: '船長', crew: '乗組員',
};

const PRESET_COLORS = [
  { label: 'ネイビー',  value: '#0057d9' },
  { label: 'スカイ',   value: '#0891b2' },
  { label: 'グリーン', value: '#059669' },
  { label: 'パープル', value: '#7c3aed' },
  { label: 'ローズ',  value: '#e11d48' },
  { label: 'オレンジ', value: '#ea580c' },
  { label: 'スレート', value: '#475569' },
  { label: 'ブラック', value: '#111827' },
];

type Tab = 'company' | 'design' | 'profile' | 'security';

const TABS: { key: Tab; icon: React.ReactNode; label: string; desc: string }[] = [
  { key: 'company',  icon: <Building2 size={16} />, label: '会社情報',     desc: '住所・TEL・代表者名' },
  { key: 'design',   icon: <Palette size={16} />,   label: 'デザイン',     desc: 'ロゴ・テーマカラー' },
  { key: 'profile',  icon: <User size={16} />,       label: 'プロフィール', desc: '名前・メールアドレス' },
  { key: 'security', icon: <Shield size={16} />,     label: 'セキュリティ', desc: 'パスワード変更' },
];

function Feedback({ ok, msg }: { ok: boolean; msg: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 8, marginTop: 14,
      background: ok ? '#f0fdf4' : '#fef2f2', color: ok ? '#16a34a' : '#dc2626', fontSize: 13, fontWeight: 600 }}>
      {ok ? <CheckCircle size={15} /> : <AlertCircle size={15} />} {msg}
    </div>
  );
}

const inp: React.CSSProperties = { width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box', background: '#fff' };
const lbl: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 };
const saveBtn = (color = '#0057d9'): React.CSSProperties => ({ padding: '9px 20px', background: color, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' });
const cancelBtn: React.CSSProperties = { padding: '9px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' };

const SettingsPage: React.FC<Props> = ({ user }) => {
  const [activeTab, setActiveTab] = useState<Tab>('company');
  const [profile, setProfile]     = useState<Profile | null>(null);
  const [loading, setLoading]     = useState(true);

  // 会社情報フォーム
  const [addr, setAddr] = useState('');
  const [tel, setTel]   = useState('');
  const [rep, setRep]   = useState('');
  const [companySaving, setCompanySaving] = useState(false);
  const [companyFb, setCompanyFb] = useState<{ ok: boolean; msg: string } | null>(null);

  // デザインフォーム
  const [color, setColor]   = useState('#0057d9');
  const [logoUrl, setLogoUrl] = useState('');
  const [designSaving, setDesignSaving] = useState(false);
  const [designFb, setDesignFb] = useState<{ ok: boolean; msg: string } | null>(null);

  // プロフィールフォーム
  const [profName, setProfName]   = useState('');
  const [profEmail, setProfEmail] = useState('');
  const [profSaving, setProfSaving] = useState(false);
  const [profFb, setProfFb] = useState<{ ok: boolean; msg: string } | null>(null);

  // セキュリティフォーム
  const [curPw, setCurPw]       = useState('');
  const [newPw, setNewPw]       = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwFb, setPwFb]         = useState<{ ok: boolean; msg: string } | null>(null);

  // MFA フォーム
  const [mfaStep, setMfaStep]   = useState<'idle' | 'setup' | 'verify' | 'done' | 'disable'>('idle');
  const [mfaQrcode, setMfaQrcode] = useState('');
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaCode, setMfaCode]   = useState('');
  const [mfaDisableCode, setMfaDisableCode] = useState('');
  const [mfaBusy, setMfaBusy]   = useState(false);
  const [mfaFb, setMfaFb]       = useState<{ ok: boolean; msg: string } | null>(null);

  const loadProfile = () => {
    setLoading(true);
    get<{ data: Profile }>('/users/me/profile').then(r => {
      const p = r.data;
      setProfile(p);
      setAddr(p.company_address ?? '');
      setTel(p.company_tel ?? '');
      setRep(p.company_rep ?? '');
      setColor(p.primary_color ?? '#0057d9');
      setLogoUrl(p.logo_url ?? '');
      setProfName(p.user_name);
      setProfEmail(p.email ?? '');
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { loadProfile(); }, []);

  // ── 会社情報保存 ──
  const saveCompany = async () => {
    setCompanySaving(true); setCompanyFb(null);
    try {
      await patch('/users/company/profile', { company_address: addr, company_tel: tel, company_rep: rep });
      setCompanyFb({ ok: true, msg: '会社情報を保存しました' });
      loadProfile();
    } catch (e: any) { setCompanyFb({ ok: false, msg: e.message ?? '保存に失敗しました' }); }
    finally { setCompanySaving(false); }
  };

  // ── デザイン保存 ──
  const saveDesign = async () => {
    setDesignSaving(true); setDesignFb(null);
    try {
      await patch('/users/company/profile', { primary_color: color, logo_url: logoUrl || null });
      // ローカルにも反映
      const stored = JSON.parse(localStorage.getItem('admin_design') ?? '{}');
      localStorage.setItem('admin_design', JSON.stringify({ ...stored, primary_color: color, logo_url: logoUrl || null }));
      document.documentElement.style.setProperty('--color-primary', color);
      setDesignFb({ ok: true, msg: 'デザイン設定を保存しました。サイドバーに反映されました' });
      loadProfile();
      window.dispatchEvent(new Event('design-updated'));
    } catch (e: any) { setDesignFb({ ok: false, msg: e.message ?? '保存に失敗しました' }); }
    finally { setDesignSaving(false); }
  };

  // ── プロフィール保存 ──
  const saveProfile = async () => {
    setProfSaving(true); setProfFb(null);
    try {
      await patch('/users/me/profile', { user_name: profName, email: profEmail || undefined });
      const stored = JSON.parse(localStorage.getItem('admin_user') ?? '{}');
      localStorage.setItem('admin_user', JSON.stringify({ ...stored, user_name: profName }));
      setProfFb({ ok: true, msg: 'プロフィールを更新しました' });
      loadProfile();
    } catch (e: any) { setProfFb({ ok: false, msg: e.message ?? '更新に失敗しました' }); }
    finally { setProfSaving(false); }
  };

  // ── パスワード変更 ──
  const savePw = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPw !== confirmPw) { setPwFb({ ok: false, msg: '新しいパスワードが一致しません' }); return; }
    if (newPw.length < 6) { setPwFb({ ok: false, msg: '6文字以上で入力してください' }); return; }
    setPwSaving(true); setPwFb(null);
    try {
      await post('/users/me/change-password', { current_password: curPw, new_password: newPw });
      setPwFb({ ok: true, msg: 'パスワードを変更しました' });
      setCurPw(''); setNewPw(''); setConfirmPw('');
    } catch (e: any) { setPwFb({ ok: false, msg: e.message ?? '変更に失敗しました' }); }
    finally { setPwSaving(false); }
  };

  // ── MFA セットアップ ──
  const startMfaSetup = async () => {
    setMfaBusy(true); setMfaFb(null);
    try {
      const data = await mfaSetup();
      setMfaQrcode(data.qrcode);
      setMfaSecret(data.secret);
      setMfaCode('');
      setMfaStep('setup');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '設定に失敗しました';
      setMfaFb({ ok: false, msg });
    } finally { setMfaBusy(false); }
  };

  const enableMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setMfaBusy(true); setMfaFb(null);
    try {
      await mfaEnable(mfaCode);
      setMfaStep('done');
      setMfaFb({ ok: true, msg: '二段階認証を有効化しました' });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '有効化に失敗しました。コードを確認してください';
      setMfaFb({ ok: false, msg });
    } finally { setMfaBusy(false); }
  };

  const disableMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setMfaBusy(true); setMfaFb(null);
    try {
      await mfaDisable(mfaDisableCode);
      setMfaStep('idle');
      setMfaDisableCode('');
      setMfaFb({ ok: true, msg: '二段階認証を無効化しました' });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '無効化に失敗しました。コードを確認してください';
      setMfaFb({ ok: false, msg });
    } finally { setMfaBusy(false); }
  };

  const primaryColor = color || '#0057d9';

  if (loading) return (
    <div className="page-wrap">
      <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>読み込み中...</div>
    </div>
  );

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <h1 className="page-title">設定</h1>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 20, alignItems: 'start' }}>

        {/* 左ナビ */}
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          {TABS.map((t, i) => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 12,
              padding: '14px 16px', border: 'none', cursor: 'pointer', textAlign: 'left',
              background: activeTab === t.key ? `${primaryColor}10` : 'transparent',
              borderLeft: `3px solid ${activeTab === t.key ? primaryColor : 'transparent'}`,
              borderBottom: i < TABS.length - 1 ? '1px solid #f3f4f6' : 'none',
              transition: 'background 0.1s',
            }}>
              <span style={{ color: activeTab === t.key ? primaryColor : '#6b7280' }}>{t.icon}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: activeTab === t.key ? 700 : 500, color: activeTab === t.key ? primaryColor : '#374151' }}>{t.label}</div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{t.desc}</div>
              </div>
            </button>
          ))}
        </div>

        {/* コンテンツ */}
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>

          {/* ── 会社情報 ── */}
          {activeTab === 'company' && (
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 4 }}>会社情報</div>
              <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 20 }}>証明書・労働記録 PDF に記載される情報を管理します</div>

              {profile && (
                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 13 }}>
                  <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>会社名（変更はSA管理者から）</div>
                  <div style={{ color: '#111827', fontSize: 14 }}>{profile.company_name}</div>
                  {profile.website_url
                    ? (
                      <a href={profile.website_url} target="_blank" rel="noreferrer"
                        style={{ fontSize: 12, color: '#0057d9', display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                        <ExternalLink size={11} /> {profile.website_url.replace(/^https?:\/\//, '')}
                      </a>
                    )
                    : <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>会社HP：未設定（SA管理者が設定できます）</div>
                  }
                </div>
              )}

              <div style={{ display: 'grid', gap: 16 }}>
                <div><label style={lbl}>住所</label><input style={inp} value={addr} onChange={e => setAddr(e.target.value)} placeholder="例: 東京都港区芝浦2-1-1" /></div>
                <div><label style={lbl}>電話番号</label><input style={inp} value={tel} onChange={e => setTel(e.target.value)} placeholder="例: 03-1234-5678" /></div>
                <div><label style={lbl}>代表者名</label><input style={inp} value={rep} onChange={e => setRep(e.target.value)} placeholder="例: 代表取締役社長　山田 義雄" /></div>
              </div>

              {(!addr || !tel || !rep) && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginTop: 16, fontSize: 12, color: '#92400e' }}>
                  ⚠ 未入力の項目は証明書PDFに「（未設定）」として表示されます
                </div>
              )}

              {companyFb && <Feedback {...companyFb} />}
              <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
                <button onClick={saveCompany} disabled={companySaving} style={saveBtn(primaryColor)}>
                  {companySaving ? '保存中...' : '保存する'}
                </button>
                <button onClick={loadProfile} style={cancelBtn}>リセット</button>
              </div>
            </div>
          )}

          {/* ── デザイン ── */}
          {activeTab === 'design' && (
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 4 }}>デザイン設定</div>
              <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 20 }}>サイドバーのアクセントカラーとロゴを設定します</div>

              {/* テーマカラー */}
              <div style={{ marginBottom: 24 }}>
                <label style={lbl}>テーマカラー</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  {PRESET_COLORS.map(p => (
                    <button key={p.value} onClick={() => setColor(p.value)} style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                      border: color === p.value ? `2px solid ${p.value}` : '2px solid #e5e7eb',
                      borderRadius: 8, padding: '8px 10px', background: '#fff', cursor: 'pointer',
                      boxShadow: color === p.value ? `0 0 0 3px ${p.value}33` : 'none',
                    }}>
                      <div style={{ width: 28, height: 28, borderRadius: 6, background: p.value }} />
                      <span style={{ fontSize: 10, color: '#6b7280' }}>{p.label}</span>
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <label style={{ ...lbl, margin: 0 }}>カスタム</label>
                  <input type="color" value={color} onChange={e => setColor(e.target.value)}
                    style={{ width: 44, height: 36, border: '1px solid #e5e7eb', borderRadius: 6, padding: 2, cursor: 'pointer' }} />
                  <input value={color} onChange={e => setColor(e.target.value)}
                    style={{ ...inp, width: 120, fontSize: 13 }} placeholder="#0057d9" />
                </div>
              </div>

              {/* ロゴURL */}
              <div style={{ marginBottom: 24 }}>
                <label style={lbl}>会社ロゴ URL（サイドバーに表示）</label>
                <input style={inp} value={logoUrl} onChange={e => setLogoUrl(e.target.value)} placeholder="https://example.com/logo.png" />
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 5 }}>
                  PNG / SVG 推奨。未設定の場合はデフォルトの錨アイコンが表示されます
                </div>
                {logoUrl && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>プレビュー</div>
                    <div style={{ width: 48, height: 48, borderRadius: 10, background: `${color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${color}44`, overflow: 'hidden' }}>
                      <img src={logoUrl} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    </div>
                  </div>
                )}
              </div>

              {/* プレビュー */}
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', marginBottom: 10 }}>サイドバープレビュー</div>
                <div style={{ width: 180, background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                  <div style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 7, background: `linear-gradient(135deg, ${color}, ${color}aa)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                      {logoUrl ? <img src={logoUrl} style={{ width: 20, height: 20, objectFit: 'contain' }} alt="" /> : '⚓'}
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 800, color: '#111827' }}>{BRAND_NAME}</div>
                      <div style={{ fontSize: 8, color: '#9ca3af' }}>{BRAND_SUB}</div>
                    </div>
                  </div>
                  {['ダッシュボード', 'GPS位置情報', '船舶管理'].map((item, i) => (
                    <div key={item} style={{ padding: '8px 12px', fontSize: 11, color: i === 0 ? color : '#374151',
                      borderLeft: i === 0 ? `3px solid ${color}` : '3px solid transparent',
                      background: i === 0 ? `${color}10` : 'transparent', fontWeight: i === 0 ? 600 : 400 }}>
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {designFb && <Feedback {...designFb} />}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={saveDesign} disabled={designSaving} style={saveBtn(primaryColor)}>
                  {designSaving ? '保存中...' : '保存して適用'}
                </button>
                <button onClick={() => { setColor(profile?.primary_color ?? '#0057d9'); setLogoUrl(profile?.logo_url ?? ''); }} style={cancelBtn}>
                  リセット
                </button>
              </div>
            </div>
          )}

          {/* ── プロフィール ── */}
          {activeTab === 'profile' && (
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 4 }}>プロフィール</div>
              <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 20 }}>表示名・メールアドレスを変更します</div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24, padding: '12px 16px', background: '#f8fafc', borderRadius: 10 }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}88)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                  {(profile?.user_name ?? user.user_name).replace(/\s/g, '').slice(0, 2)}
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{profile?.user_name ?? user.user_name}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                    {ROLE_LABEL[profile?.user_role ?? ''] ?? profile?.user_role}
                    {profile?.employee_code && ` · ${profile.employee_code}`}
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                    最終ログイン: {profile?.last_login_at ? profile.last_login_at.slice(0, 16).replace('T', ' ') : '—'}
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gap: 16 }}>
                <div><label style={lbl}>表示名</label><input style={inp} value={profName} onChange={e => setProfName(e.target.value)} /></div>
                <div><label style={lbl}>メールアドレス</label><input type="email" style={inp} value={profEmail} onChange={e => setProfEmail(e.target.value)} placeholder="例: yamada@example.com" /></div>
              </div>

              {profFb && <Feedback {...profFb} />}
              <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
                <button onClick={saveProfile} disabled={profSaving} style={saveBtn(primaryColor)}>
                  {profSaving ? '保存中...' : '更新する'}
                </button>
                <button onClick={() => { setProfName(profile?.user_name ?? ''); setProfEmail(profile?.email ?? ''); }} style={cancelBtn}>
                  リセット
                </button>
              </div>
            </div>
          )}

          {/* ── セキュリティ ── */}
          {activeTab === 'security' && (
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 4 }}>セキュリティ</div>
              <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 20 }}>パスワードを変更します。6文字以上で設定してください</div>

              <form onSubmit={savePw}>
                <div style={{ display: 'grid', gap: 16, marginBottom: 4 }}>
                  <div><label style={lbl}>現在のパスワード</label><PasswordInput style={inp} value={curPw} onChange={e => setCurPw(e.target.value)} required /></div>
                  <div><label style={lbl}>新しいパスワード（6文字以上）</label><PasswordInput style={inp} value={newPw} onChange={e => setNewPw(e.target.value)} required minLength={6} /></div>
                  <div>
                    <label style={lbl}>新しいパスワード（確認）</label>
                    <PasswordInput style={{ ...inp, borderColor: confirmPw && confirmPw !== newPw ? '#ef4444' : '#e5e7eb' }}
                      value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required />
                    {confirmPw && confirmPw !== newPw && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>パスワードが一致しません</div>}
                  </div>
                </div>

                {pwFb && <Feedback {...pwFb} />}
                <div style={{ marginTop: 18 }}>
                  <button type="submit" disabled={pwSaving} style={saveBtn(primaryColor)}>
                    {pwSaving ? '変更中...' : 'パスワードを変更する'}
                  </button>
                </div>
              </form>

              <div style={{ marginTop: 24, padding: '14px 16px', background: '#f8fafc', borderRadius: 10, fontSize: 12, color: '#6b7280', lineHeight: 1.7 }}>
                <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>セキュリティのヒント</div>
                <div>• 英数字・記号を組み合わせた8文字以上を推奨します</div>
                <div>• 他のサービスと同じパスワードは使用しないでください</div>
                <div>• 変更後も現在のセッションは継続されます</div>
              </div>

              {/* ── MFA 設定セクション ── */}
              <div style={{ background: '#fff', borderRadius: 12, padding: '24px', border: '1px solid #e2e8f0', marginTop: 28 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4, marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Shield size={16} /> 二段階認証（MFA）
                </h3>
                <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
                  認証アプリ（Google Authenticator など）を使用して、ログイン時にワンタイムコードによる追加認証を行います。
                </p>

                {/* idle: MFAを設定する */}
                {mfaStep === 'idle' && (
                  <div>
                    <button onClick={startMfaSetup} disabled={mfaBusy}
                      style={{ padding: '9px 20px', background: primaryColor, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: mfaBusy ? 'not-allowed' : 'pointer', opacity: mfaBusy ? 0.7 : 1 }}>
                      {mfaBusy ? '準備中...' : 'MFAを設定する'}
                    </button>
                    <button onClick={() => { setMfaStep('disable'); setMfaFb(null); setMfaDisableCode(''); }}
                      style={{ marginLeft: 8, padding: '9px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                      MFAを無効にする
                    </button>
                  </div>
                )}

                {/* setup: QRコード表示 */}
                {mfaStep === 'setup' && (
                  <div>
                    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>QRコードをスキャン</div>
                        {mfaQrcode && (
                          <img src={mfaQrcode} alt="MFA QR Code"
                            style={{ width: 160, height: 160, border: '1px solid #e5e7eb', borderRadius: 8, display: 'block' }} />
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>手動入力キー</div>
                        <div style={{ fontFamily: 'monospace', fontSize: 13, background: '#f1f5f9', padding: '8px 12px', borderRadius: 6, color: '#0f172a', wordBreak: 'break-all', marginBottom: 12 }}>
                          {mfaSecret}
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.6 }}>
                          <div>1. Google Authenticator などの認証アプリを開く</div>
                          <div>2. QRコードをスキャン（または上記キーを手動入力）</div>
                          <div>3. 表示された6桁のコードを入力して有効化</div>
                        </div>
                      </div>
                    </div>
                    <form onSubmit={enableMfa} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                      <div style={{ flex: 1, maxWidth: 200 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>認証コード（6桁）</label>
                        <input type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                          value={mfaCode} onChange={e => setMfaCode(e.target.value.replace(/\D/g, ''))}
                          placeholder="000000" required autoComplete="one-time-code"
                          style={{ width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '9px 12px', fontSize: 18, fontWeight: 700, letterSpacing: '0.2em', outline: 'none', boxSizing: 'border-box', textAlign: 'center' }} />
                      </div>
                      <button type="submit" disabled={mfaBusy || mfaCode.length !== 6}
                        style={{ padding: '9px 20px', background: (mfaBusy || mfaCode.length !== 6) ? '#94a3b8' : primaryColor, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: (mfaBusy || mfaCode.length !== 6) ? 'not-allowed' : 'pointer' }}>
                        {mfaBusy ? '有効化中...' : '有効化する'}
                      </button>
                      <button type="button" onClick={() => { setMfaStep('idle'); setMfaFb(null); }}
                        style={{ padding: '9px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                        キャンセル
                      </button>
                    </form>
                  </div>
                )}

                {/* done: 有効化済み */}
                {mfaStep === 'done' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <CheckCircle size={18} color="#16a34a" />
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#16a34a' }}>二段階認証が有効です</span>
                    <button onClick={() => { setMfaStep('disable'); setMfaFb(null); setMfaDisableCode(''); }}
                      style={{ marginLeft: 12, padding: '7px 14px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>
                      無効にする
                    </button>
                  </div>
                )}

                {/* disable: MFA無効化 */}
                {mfaStep === 'disable' && (
                  <form onSubmit={disableMfa}>
                    <div style={{ fontSize: 13, color: '#374151', marginBottom: 12 }}>
                      現在の認証コードを入力してMFAを無効化します。
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                      <div style={{ flex: 1, maxWidth: 200 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>認証コード（6桁）</label>
                        <input type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                          value={mfaDisableCode} onChange={e => setMfaDisableCode(e.target.value.replace(/\D/g, ''))}
                          placeholder="000000" required
                          style={{ width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '9px 12px', fontSize: 18, fontWeight: 700, letterSpacing: '0.2em', outline: 'none', boxSizing: 'border-box', textAlign: 'center' }} />
                      </div>
                      <button type="submit" disabled={mfaBusy || mfaDisableCode.length !== 6}
                        style={{ padding: '9px 20px', background: (mfaBusy || mfaDisableCode.length !== 6) ? '#94a3b8' : '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: (mfaBusy || mfaDisableCode.length !== 6) ? 'not-allowed' : 'pointer' }}>
                        {mfaBusy ? '無効化中...' : '無効化する'}
                      </button>
                      <button type="button" onClick={() => { setMfaStep('idle'); setMfaFb(null); }}
                        style={{ padding: '9px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                        キャンセル
                      </button>
                    </div>
                  </form>
                )}

                {mfaFb && <Feedback {...mfaFb} />}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
